import { useMutation } from '@apollo/react-hooks';
import { useCallback } from 'react';
import { useCountry } from '../../components/CountrySelector';
import { useOrderSummary } from '../useOrderSummary';
import {
  UPDATE_ORDER_MUTATION,
  UpdateOrderMutationVariables,
  UpdateOrderMutation,
  UpdateOrderAction,
  OrderState
} from './useUpdateOrder.rest';

export const useUpdateOrder = (orderNumber: string) => {
  const {
    country: { alpha3Code }
  } = useCountry();

  const { orderSummary } = useOrderSummary(orderNumber);

  const [mutate, { loading, error }] = useMutation<
    UpdateOrderMutation,
    UpdateOrderMutationVariables
  >(UPDATE_ORDER_MUTATION);

  const setOrderStateToDefault = useCallback(async () => {
    if (!orderSummary) return;

    await mutate({
      variables: {
        orderId: orderSummary.id,
        input: {
          data: {
            // eslint-disable-next-line @typescript-eslint/camelcase
            action_type: UpdateOrderAction.UpdatePayment,
            id: orderSummary.id,
            type: 'order_summary',
            attributes: {
              // eslint-disable-next-line @typescript-eslint/camelcase
              country_code: alpha3Code,
              // eslint-disable-next-line @typescript-eslint/camelcase
              order_state: OrderState.Default
            }
          }
        }
      }
    });
  }, [alpha3Code, mutate, orderSummary]);

  const setOrderStateToPaying = useCallback(async () => {
    if (!orderSummary) return;

    await mutate({
      variables: {
        orderId: orderSummary.id,
        input: {
          data: {
            // eslint-disable-next-line @typescript-eslint/camelcase
            action_type: UpdateOrderAction.UpdatePayment,
            id: orderSummary.id,
            type: 'order_summary',
            attributes: {
              // eslint-disable-next-line @typescript-eslint/camelcase
              country_code: alpha3Code,
              // eslint-disable-next-line @typescript-eslint/camelcase
              order_state: OrderState.PayRemainingBalance
            }
          }
        }
      }
    });
  }, [alpha3Code, mutate, orderSummary]);

  const updateOrderSelectedItems = useCallback(
    async (selectedItemsIds: number[]) => {
      if (!orderSummary) return;

      await mutate({
        variables: {
          orderId: orderSummary.id,
          input: {
            data: {
              // eslint-disable-next-line @typescript-eslint/camelcase
              action_type: UpdateOrderAction.UpdatePayment,
              id: orderSummary.id,
              type: 'order_summary',
              attributes: {
                // eslint-disable-next-line @typescript-eslint/camelcase
                country_code: alpha3Code,
                // eslint-disable-next-line @typescript-eslint/camelcase
                order_state: OrderState.PayRemainingBalance,
                // eslint-disable-next-line @typescript-eslint/camelcase
                selected_items_ids: selectedItemsIds
              }
            }
          }
        }
      });
    },
    [alpha3Code, mutate, orderSummary]
  );

  return {
    updateOrderSelectedItems,
    setOrderStateToDefault,
    setOrderStateToPaying,
    loading,
    error
  };
};
